import { Injectable } from '@angular/core';
import {
  IWorkflowAction,
  IWorkflowCondition,
  IWorkflowConditionGroup,
  IWorkflowRule,
  IWorkflowTemplate,
} from '../models/workflows.models';

/**
 * Builds the rule the builder starts from, for both entry paths.
 *
 * The template catalog itself is backend-owned (GET /v1/workflow-rules/templates,
 * loaded into the store) — this service only turns a fetched template into a
 * client-side rule draft, minting fresh ids so the builder can track rows before
 * anything is persisted.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowTemplatesService {
  /** Hydrate a fetched template into an editable rule draft.
   *
   *  Deep-cloned: the store's template objects must not be mutated by the builder.
   *  `name` is prefilled by the caller from the template's translated
   *  `i18nDefaultName`, so the rule is immediately savable. */
  buildRuleFromTemplate(template: IWorkflowTemplate, name = ''): Partial<IWorkflowRule> {
    return {
      name,
      trigger: {
        type: template.trigger.type,
        params: { ...(template.trigger.params ?? {}) },
      },
      conditionGroups: (template.conditionGroups ?? []).map((group) =>
        this.buildConditionGroup(
          (group.conditions ?? []).map((condition) =>
            this.buildCondition(condition.field, condition.operator, condition.value),
          ),
          group.match,
        ),
      ),
      actions: (template.actions ?? []).map((action, index) =>
        this.buildAction(action.type, index, action.params),
      ),
    };
  }

  buildEmptyRule(): Partial<IWorkflowRule> {
    return {
      name: '',
      trigger: { type: 'new_email_received', params: {} },
      conditionGroups: [],
      actions: [],
    };
  }

  private buildConditionGroup(
    conditions: IWorkflowCondition[],
    match: IWorkflowConditionGroup['match'] = 'all',
  ): IWorkflowConditionGroup {
    return {
      id: this.buildTempId('tpl-cg'),
      match,
      conditions,
    };
  }

  private buildCondition(
    field: IWorkflowCondition['field'],
    operator: IWorkflowCondition['operator'],
    value: IWorkflowCondition['value'],
  ): IWorkflowCondition {
    return {
      id: this.buildTempId('tpl-c'),
      field,
      operator,
      value,
    };
  }

  private buildAction(type: IWorkflowAction['type'], order: number, params?: IWorkflowAction['params']): IWorkflowAction {
    return {
      id: this.buildTempId('tpl-a'),
      type,
      params: { ...(params ?? {}) },
      order,
    };
  }

  private buildTempId(prefix: 'tpl-cg' | 'tpl-c' | 'tpl-a'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }
}
